"use client";

import React, { useState, useMemo, memo } from "react";
import Image from "next/image";
import {
  isYouTube,
  PlayIcon,
  getYouTubeEmbed,
  getYouTubeThumb,
ContentOverlay
} from "./utils";


function MediaCard({ item, index, isLarge, courseVideoUrl }) {
  const [isPlaying, setIsPlaying] = useState(false);

  // ✅ Memoize heavy logic
  const {
    mediaUrl,
    isVideo,
    isYouTubeUrl,
    isVideoFile,
  } = useMemo(() => {
    const candidateUrl = isLarge
      ? courseVideoUrl || item?.MediaURL || item?.Media?.[0]?.url
      : item?.Media?.[0]?.url || item?.MediaURL || null;

    const isYouTubeUrl = isYouTube(candidateUrl);
    const isVideoMime = item?.Media?.[0]?.mime?.startsWith("video/");
    const isVideoFile =
      isVideoMime ||
      (!!candidateUrl &&
        /\.(mp4|webm|ogg|mov)(?:\?|$)/i.test(candidateUrl));

    return {
      mediaUrl: candidateUrl,
      isYouTubeUrl,
      isVideoFile,
      isVideo: Boolean(isLarge && (isYouTubeUrl || isVideoFile)),
    };
  }, [item, isLarge, courseVideoUrl]);

  const handlePlay = (e) => {
    e.stopPropagation();
    setIsPlaying(true);
  };

  const handleClose = () => setIsPlaying(false);

  // ✅ Thumbnail (NO VIDEO LOAD)
  const thumbnail = isYouTubeUrl
    ? getYouTubeThumb(mediaUrl)
    : mediaUrl;

  return (
    <div
      className={`relative overflow-hidden cursor-pointer group ${
        isLarge
          ? "h-[220px] sm:h-[260px] lg:h-full lg:min-h-[400px]"
          : "h-[180px] sm:h-[220px] lg:h-[250px]"
      }`}
    >
      {/* 🎥 VIDEO MODE */}
      {isVideo ? (
        !isPlaying ? (
          <div className="relative w-full h-full">
            {/* ✅ Optimized Image */}
            <Image
              src={thumbnail || "/placeholder.jpg"}
              alt={item?.ImageBlockHeading || ""}
              fill
              className="object-cover"
              sizes="(max-width:768px)100vw, 50vw"
              priority={index === 0}
              quality={75}
            />

            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

            {/* Play Button */}
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center hover:scale-110 transition">
                <PlayIcon />
              </div>
            </button>

            <ContentOverlay title={item?.ImageBlockHeading} />
          </div>
        ) : (
          <div className="relative w-full h-full bg-black">
            {isYouTubeUrl ? (
              <iframe
                src={getYouTubeEmbed(mediaUrl)}
                className="w-full h-full"
                loading="lazy"
                allowFullScreen
              />
            ) : (
              <video
                src={mediaUrl}
                className="w-full h-full object-cover"
                controls
                autoPlay
                playsInline
              />
            )}

            <button
              onClick={handleClose}
              className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded"
            >
              ✕
            </button>
          </div>
        )
      ) : (
        // 🖼 IMAGE MODE
        <div className="relative w-full h-full">
          <Image
            src={thumbnail || "/placeholder.jpg"}
            alt={item?.ImageBlockHeading || ""}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width:768px)100vw, 33vw"
            quality={75}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

          {(isYouTubeUrl || isVideoFile) && (
            <button
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center"
            >
              <PlayIcon />
            </button>
          )}

          <ContentOverlay title={item?.ImageBlockHeading} />
        </div>
      )}
    </div>
  );
}

// ✅ Prevent unnecessary re-renders
export default memo(MediaCard);